import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "../assests/logo.png";
import { useAuth } from "../auth/AuthContext";
import { api } from "../services/api";
import {
  Bell,
  BellRing,
  BookOpen,
  FilePenLine,
  Image as ImageIcon,
  ArrowLeft,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  ExternalLink,
  FileText,
  FolderTree,
  History,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  MailCheck,
  Menu,
  Package,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  Star,
  Undo2,
  Users,
  Warehouse,
  Workflow,
  X,
} from "lucide-react";

const MENU_LINKS = [
  { label: "Dashboard", href: "/dashboard", icon: LayoutDashboard, permission: "dashboard" },
  { label: "Products / Inventory", href: "/dashboard/products", icon: Package, permission: "products" },
  { label: "Product Categories", href: "/dashboard/categories", icon: FolderTree, permission: "products" },
  { label: "Orders", href: "/dashboard/orders", icon: ClipboardList, permission: "orders" },
  { label: "Returns", href: "/dashboard/returns", icon: Undo2, permission: "returns" },
  { label: "Channel Sync", href: "/dashboard/sync", icon: RefreshCw, permission: "channels" },
  { label: "Warehouse Locations", href: "/dashboard/warehouse", icon: Warehouse, permission: "warehouse" },
  { label: "Tax & Reports", href: "/dashboard/reports", icon: FileText, permission: "reports" },
  { label: "Part Books", href: "/dashboard/part-books", icon: BookOpen, permission: "products" },
  { label: "Website CMS", href: "/dashboard/cms", icon: FilePenLine, permission: "cms" },
  { label: "Media Library", href: "/dashboard/media", icon: ImageIcon, permission: "media" },
  { label: "Custom Order", href: "/dashboard/custom-order", icon: ClipboardList, permission: "orders" },
  { label: "Customer Accounts", href: "/dashboard/customers", icon: Users, permission: "customers" },
  { label: "Product Reviews", href: "/dashboard/reviews", icon: Star, permission: "reviews" },
];

const GENERAL_LINKS = [
  { label: "Notifications Center", href: "/dashboard/notifications", icon: BellRing, permission: "notifications" },
  { label: "Newsletter", href: "/dashboard/newsletter", icon: MailCheck, permission: "newsletter" },
  { label: "Activity Logs", href: "/dashboard/activity", icon: History, permission: "activity" },
  { label: "Help / Support", href: "/dashboard/support", icon: LifeBuoy, permission: "support" },
  { label: "Automation", href: "/dashboard/automation", icon: Workflow, permission: "automation" },
  { label: "Settings", href: "/dashboard/settings", icon: Settings, permission: "settings" },
];

const SUPER_ADMIN_EMAIL = "admin@bobbinusa.com";

function canAccessLink(user, link) {
  if (user?.role !== "admin") return false;
  if (user?.email === SUPER_ADMIN_EMAIL) return true;
  const privileges = user.privileges || {};
  const hasAssignedPrivileges = Object.values(privileges).some(Boolean);
  if (!hasAssignedPrivileges) return true;
  return Boolean(privileges[link.permission]);
}

function findRoutePermission(pathname) {
  const links = [...MENU_LINKS, ...GENERAL_LINKS]
    .filter((link) => link.href !== "/dashboard")
    .sort((a, b) => b.href.length - a.href.length);
  const match = links.find((link) => pathname === link.href || pathname.startsWith(`${link.href}/`));
  return match?.permission || "dashboard";
}

function flattenCategoryTree(items = [], depth = 0, parentNames = []) {
  return items.flatMap((category) => {
    const children = category.subcategories || category.children || [];
    const item = {
      ...category,
      depth,
      pathLabel: [...parentNames, category.name].filter(Boolean).join(" / "),
    };

    return [item, ...flattenCategoryTree(children, depth + 1, [...parentNames, category.name])];
  });
}

function getCategoryKey(category = {}) {
  return String(category._id || category.wcId || category.slug || category.name || "");
}

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [search, setSearch] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [catalogResults, setCatalogResults] = useState([]);
  const [catalogSearchLoading, setCatalogSearchLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const menuLinks = useMemo(() => MENU_LINKS.filter((link) => canAccessLink(user, link)), [user]);
  const generalLinks = useMemo(() => GENERAL_LINKS.filter((link) => canAccessLink(user, link)), [user]);
  const allLinks = useMemo(() => [...menuLinks, ...generalLinks], [generalLinks, menuLinks]);
  const currentPermission = findRoutePermission(location.pathname);
  const canViewCurrentPage = canAccessLink(user, { permission: currentPermission });

  useEffect(() => {
    const stored = window.localStorage.getItem("dashboard-sidebar-collapsed");
    if (stored !== null) {
      setSidebarCollapsed(stored === "true");
    }
  }, []);

  useEffect(() => {
    window.localStorage.setItem("dashboard-sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await api.get("/notifications?status=open&limit=5&refresh=false");
      setNotifications(data.notifications || []);
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 60000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  const pageSearchResults = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];

    return allLinks
      .filter((link) => link.label.toLowerCase().includes(term) || link.href.toLowerCase().includes(term))
      .slice(0, 4)
      .map((link) => ({
        ...link,
        key: `page:${link.href}`,
        type: "Page",
        description: link.href.replace("/dashboard/", "").replace("/dashboard", "Dashboard"),
      }));
  }, [allLinks, search]);

  useEffect(() => {
    const term = search.trim();

    if (term.length < 2) {
      setCatalogResults([]);
      setCatalogSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setCatalogSearchLoading(true);

      try {
        const [productResponse, categoryResponse] = await Promise.all([
          api.get(`/products?search=${encodeURIComponent(term)}&limit=6`).catch(() => null),
          api.get("/product-categories").catch(() => null),
        ]);

        if (cancelled) return;

        const products = Array.isArray(productResponse?.products)
          ? productResponse.products.slice(0, 5).map((product) => ({
              key: `product:${product._id || product.id}`,
              href: `/dashboard/products/${product._id || product.id}/edit`,
              icon: Package,
              label: product.name || product.sku || "Untitled product",
              description: [product.sku, product.brandLine, product.category].filter(Boolean).join(" / "),
              type: "Product",
            }))
          : [];

        const categoryTerm = term.toLowerCase();
        const categories = flattenCategoryTree(categoryResponse?.categories || [])
          .filter((category) =>
            [category.name, category.slug, category.pathLabel, category.wcId]
              .join(" ")
              .toLowerCase()
              .includes(categoryTerm)
          )
          .slice(0, 5)
          .map((category) => ({
            key: `category:${getCategoryKey(category)}`,
            href: `/dashboard/categories?edit=${encodeURIComponent(getCategoryKey(category))}`,
            icon: FolderTree,
            label: category.name || "Untitled category",
            description: category.pathLabel || category.slug || "Product category",
            type: "Category",
          }));

        setCatalogResults([...products, ...categories].slice(0, 8));
      } finally {
        if (!cancelled) setCatalogSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [search]);

  const searchResults = useMemo(() => {
    const seen = new Set();

    return [...catalogResults, ...pageSearchResults].filter((result) => {
      if (seen.has(result.key)) return false;
      seen.add(result.key);
      return true;
    }).slice(0, 10);
  }, [catalogResults, pageSearchResults]);

  const handleLogout = () => {
    logout();
    navigate("/account");
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const firstResult = searchResults[0];
    if (firstResult) {
      navigate(firstResult.href);
      setSearch("");
      setSearchOpen(false);
      return;
    }

    navigate("/dashboard/products");
    setSearch("");
    setSearchOpen(false);
  };

  const openNotifications = () => {
    setNotificationsOpen((open) => !open);
    loadNotifications();
  };

  const sidebarItemClass = (isActive) =>
    `group flex items-center rounded-xl px-4 py-2.5 text-sm font-medium transition-all ${isActive ? "bg-white text-[#4391d1] font-bold shadow-sm" : "text-white hover:bg-white/10"
    } ${sidebarCollapsed ? "md:justify-center md:px-3" : "gap-3"}`;

  return (
    <div className="dashboard-shell flex h-screen overflow-hidden gap-4 bg-gray-100 p-4 font-gopher md:gap-6 md:p-3">
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setMobileOpen(false)} />
      )}

      <aside
        className={`
          fixed left-0 top-0 z-50 flex h-[calc(100vh-40px)] flex-shrink-0 flex-col overflow-y-auto overflow-x-hidden
          rounded-none bg-[#4391d1] text-white shadow-xl transition-all duration-300 ease-out md:relative md:z-auto 
          w-72 ${sidebarCollapsed ? "md:w-[3.5rem] md:rounded-3xl md:rounded-bl-3xl" : "md:w-72 md:rounded-3xl"}
          ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        <div className={`flex items-center gap-3 border-b border-white/10 px-5 py-4 ${sidebarCollapsed ? "md:justify-center md:px-3" : ""}`}>
          <Link
            aria-label="Visit website"
            className={sidebarCollapsed ? "hidden" : "inline-flex"}
            onClick={() => setMobileOpen(false)}
            to="/"
            title="Visit website"
          >
            <img
              src={Logo}
              alt="Bobbin USA"
              className="h-[54px] w-auto object-contain"
            />
          </Link>
          {/* <div className={`min-w-0 ${sidebarCollapsed ? "md:hidden" : ""}`}>
            <p className="truncate text-xs font-black uppercase tracking-[0.22em] text-white/70">Dashboard</p>
            <p className="truncate text-sm font-bold text-white">Control center</p>
          </div> */}
          <button
            className={`${!sidebarCollapsed && 'hidden'} rounded-xl bg-white/10 p-2 text-white/80 transition hover:bg-white/20 hover:text-white`}
            onClick={() => setSidebarCollapsed((current) => !current)}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            type="button"
          >
            {sidebarCollapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
          </button>
          <button
            className="ml-auto rounded-xl bg-white/10 p-2 text-white/80 transition hover:bg-white/20 hover:text-white md:hidden"
            onClick={() => setMobileOpen(false)}
            title="Close sidebar"
            type="button"
          >
            <X size={18} />
          </button>
        </div>

        <div className={`flex flex-col flex-1 p-4 ${sidebarCollapsed ? "md:px-2" : "md:p-6"}`}>
          <span className={`px-2 text-[10px] font-semibold uppercase tracking-widest text-white/70 ${sidebarCollapsed ? "md:hidden" : ""}`}>Menu</span>

          <nav>
            {menuLinks.map(({ label, href, icon: Icon }) => (
              <NavLink
                key={href}
                to={href}
                end={href === "/dashboard"}
                onClick={() => setMobileOpen(false)}
                title={label}
                aria-label={label}
                className={({ isActive }) => sidebarItemClass(isActive)}
              >
                <Icon size={16} className="flex-shrink-0" />
                <span className={`min-w-0 truncate ${sidebarCollapsed ? "md:hidden" : ""}`}>{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className={!sidebarCollapsed && 'mt-8'}>
            <span className={`px-2 text-[10px] font-semibold uppercase tracking-widest text-white/70 ${sidebarCollapsed ? "md:hidden" : ""}`}>General</span>
            <nav className={`${!sidebarCollapsed && 'mt-8'} space-y-0.5`}>
              {generalLinks.map(({ label, href, icon: Icon }) => (
                <NavLink
                  key={href}
                  to={href}
                  onClick={() => setMobileOpen(false)}
                  title={label}
                  aria-label={label}
                  className={({ isActive }) => sidebarItemClass(isActive)}
                >
                  <Icon size={16} className="flex-shrink-0" />
                  <span className={`min-w-0 truncate ${sidebarCollapsed ? "md:hidden" : ""}`}>{label}</span>
                </NavLink>
              ))}
              <button
                onClick={handleLogout}
                className={`flex w-full items-center rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10 ${sidebarCollapsed ? "md:justify-center md:px-3" : "gap-3"
                  }`}
                title="Logout"
                type="button"
              >
                <LogOut size={16} className="flex-shrink-0" />
                <span className={sidebarCollapsed ? "md:hidden" : ""}>Logout</span>
              </button>
            </nav>
          </div>
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-3xl bg-white shadow-sm" style={{ height: "calc(100vh - 3rem)" }}>
        <header className="flex h-20 flex-shrink-0 items-center justify-between gap-5 border-b border-gray-100 px-6 md:px-10">
          <div className="flex flex-1 items-center justify-between gap-3">
            <button className="text-gray-500 hover:text-gray-800 md:hidden" onClick={() => setMobileOpen(true)} type="button">
              <Menu size={22} />
            </button>
            {!sidebarCollapsed && <button
              className="hidden rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-[#4391d1] hover:text-[#4391d1] md:inline-flex"
              onClick={() => setSidebarCollapsed((current) => !current)}
              title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              type="button"
            >
              {sidebarCollapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
            </button>}
            <button
              className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-black text-gray-600 transition hover:border-[#4391d1] hover:text-[#4391d1]"
              onClick={() => navigate(-1)}
              title="Go back"
              type="button"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Back</span>
            </button>
            <Link
              aria-label="Visit website"
              className={`items-center ${!sidebarCollapsed ? "hidden" : "hidden sm:inline-flex"}`}
              to="/"
              title="Visit website"
            >
              <img
                src={Logo}
                alt="Bobbin USA"
                className="h-[54px] w-auto object-contain"
              />
            </Link>
            <form className="relative hidden w-64 sm:block md:w-[60%]" onSubmit={submitSearch}>
              <input
                className="w-full rounded-full border border-gray-200 bg-gray-50 py-2.5 pl-5 pr-12 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#4391d1]"
                onChange={(event) => {
                  setSearch(event.target.value);
                  setSearchOpen(true);
                }}
                onFocus={() => setSearchOpen(true)}
                placeholder="Search product, SKU, category..."
                type="text"
                value={search}
              />
              <button className="absolute right-1 top-1 rounded-full bg-[#4391d1] p-[10px] text-white transition-colors hover:bg-blue-600" type="submit">
                <Search size={14} />
              </button>
              {searchOpen && search && (
                <div className="absolute left-0 right-0 top-12 z-50 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl">
                  {catalogSearchLoading && (
                    <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3 text-xs font-black text-gray-500">
                      <RefreshCw className="h-4 w-4 animate-spin text-[#4391d1]" />
                      Searching products and categories
                    </div>
                  )}
                  {searchResults.length ? (
                    searchResults.map(({ description, href, icon: Icon, key, label, type }) => (
                      <button
                        key={key}
                        className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-bold text-gray-700 hover:bg-blue-50 hover:text-[#4391d1]"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          navigate(href);
                          setSearch("");
                          setSearchOpen(false);
                        }}
                        type="button"
                      >
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#4391d1]">
                          <Icon size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{label}</span>
                          <span className="mt-0.5 block truncate text-xs font-semibold text-gray-400">{description}</span>
                        </span>
                        <span className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-black uppercase text-gray-500">
                          {type}
                        </span>
                      </button>
                    ))
                  ) : (
                    <button
                      className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-bold text-gray-700 hover:bg-blue-50 hover:text-[#4391d1]"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        navigate("/dashboard/products");
                        setSearch("");
                        setSearchOpen(false);
                      }}
                      type="button"
                    >
                      <Package size={16} />
                      Open product listing
                    </button>
                  )}
                </div>
              )}
            </form>
          </div>

          <div className="flex items-center gap-3 md:gap-6">
            <Link
              className="inline-flex items-center gap-2 rounded-xl bg-[#4391d1] px-3 py-2 text-sm font-black text-white transition hover:bg-blue-600 sm:px-4"
              to="/"
            >
              <ExternalLink size={16} />
              <span className="hidden sm:inline">Visit Website</span>
            </Link>
            <div className="relative">
              <button
                className="relative rounded-full p-2 text-gray-600 hover:bg-gray-50 hover:text-[#4391d1]"
                onClick={openNotifications}
                type="button"
              >
                <Bell size={22} />
                {notifications.length > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-red-500 px-1 text-[10px] font-black text-white">
                    {notifications.length}
                  </span>
                )}
              </button>
              {notificationsOpen && (
                <div className="absolute right-0 top-12 z-50 w-80 overflow-hidden rounded-2xl border border-gray-100 bg-white text-left shadow-xl">
                  <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                    <p className="text-sm font-black text-gray-900">Open Alerts</p>
                    <button
                      className="text-xs font-black text-[#4391d1]"
                      onClick={() => {
                        navigate("/dashboard/notifications");
                        setNotificationsOpen(false);
                      }}
                      type="button"
                    >
                      View all
                    </button>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length ? (
                      notifications.map((notification) => (
                        <button
                          key={notification._id}
                          className="block w-full border-b border-gray-50 px-4 py-3 text-left hover:bg-blue-50"
                          onClick={() => {
                            navigate("/dashboard/notifications");
                            setNotificationsOpen(false);
                          }}
                          type="button"
                        >
                          <p className="text-sm font-black text-gray-900">{notification.title}</p>
                          <p className="mt-1 line-clamp-2 text-xs font-semibold text-gray-500">{notification.message}</p>
                          <p className="mt-2 text-[11px] font-black uppercase text-[#4391d1]">
                            {notification.type} · {notification.priority}
                          </p>
                        </button>
                      ))
                    ) : (
                      <p className="px-4 py-5 text-sm font-bold text-gray-500">No open alerts.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden text-right md:block">
                <p className="mb-0 text-sm font-bold text-gray-800">{user?.name || "Admin"}</p>
                <p className="mb-0 text-xs text-gray-500">{user?.email}</p>
              </div>
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#4391d1] text-sm font-bold text-white">
                {user?.name?.slice(0, 2).toUpperCase() || "AD"}
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-gray-50/50" data-page-scroll-container>
          {canViewCurrentPage ? (
            <Outlet />
          ) : (
            <div className="p-6">
              <section className="rounded-3xl border border-red-100 bg-white p-8 text-center shadow-sm">
                <ShieldCheck className="mx-auto text-red-400" size={38} />
                <h1 className="mt-4 text-2xl font-black text-gray-900">Access restricted</h1>
                <p className="mx-auto mt-2 max-w-xl text-sm font-semibold text-gray-500">
                  Your admin role does not include this dashboard section. Ask an owner to update your privileges in Settings.
                </p>
                <button
                  className="mt-5 rounded-xl bg-[#4391d1] px-5 py-3 text-sm font-black text-white hover:bg-blue-600"
                  onClick={() => navigate("/dashboard")}
                  type="button"
                >
                  Back to Dashboard
                </button>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
